import { useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";

import { CUSTOMER_QUERY_KEYS } from "common/constants";
import type {
  DetailedWorkspace,
  Workspace,
  WorkspaceAccount,
} from "common/types";

import getQueryKeys from "./getQueryKeys";

export default function useWorkspaceCacheUpdate() {
  const queryClient = useQueryClient();

  function updateCachedWorkspaceDetails(
    workspaceId: string,
    updater: (workspace: DetailedWorkspace) => void,
  ) {
    const detailedKey = CUSTOMER_QUERY_KEYS.workspaces.details(workspaceId);

    if (queryClient.getQueryData<DetailedWorkspace>(detailedKey)) {
      queryClient.setQueryData(
        detailedKey,
        (prevWorkspace: DetailedWorkspace) => produce(prevWorkspace, updater),
      );
    }
  }

  function updateCachedWorkspaceList(
    workspaceId: string,
    updater: (workspace: Workspace) => void,
  ) {
    queryClient.setQueryData(
      CUSTOMER_QUERY_KEYS.workspaces.base,
      (prevWorkspaces: Workspace[]) =>
        produce(prevWorkspaces, (draftWorkspaces) => {
          const workspace = draftWorkspaces.find(
            ({ id }) => id === workspaceId,
          );
          if (workspace) {
            updater(workspace);
          }
        }),
    );
  }

  function updateCachedWorkspaceAccounts(
    workspaceId: string,
    updateWorkspaceAccounts: (accounts: WorkspaceAccount[]) => void,
  ) {
    const queryKey = getQueryKeys(workspaceId).workspaceKeys.accounts;

    if (queryClient.getQueryData(queryKey)) {
      queryClient.setQueryData(queryKey, (prevAccounts: WorkspaceAccount[]) =>
        produce(prevAccounts, updateWorkspaceAccounts),
      );
    }
  }

  return {
    updateCachedWorkspaceDetails,
    updateCachedWorkspaceList,
    updateCachedWorkspaceAccounts,
  };
}
